import { useCallback, useEffect, useMemo, useState } from 'react'
import { getRequisites, type RequisitesCompany, type RequisitesResponse } from '../api'
import { CABINET_URL } from '../env'
import { copyToClipboard, insertTextAtCursor } from '../word'
import { t } from '../i18n'

interface Props {
  token: string
  refreshEpoch?: number
}

function companyLabel(company: RequisitesCompany, index: number): string {
  return company.title.trim() || company.legal_name.trim() || t('Компанія {n}', { n: index + 1 })
}

export function RequisitesPanel({ token, refreshEpoch = 0 }: Props) {
  const [data, setData] = useState<RequisitesResponse | null>(null)
  const [selectedId, setSelectedId] = useState<string>('')
  const [error, setError] = useState<string | null>(null)
  const [status, setStatus] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const load = useCallback(async () => {
    setBusy(true)
    setError(null)
    try {
      const next = await getRequisites(token)
      setData(next)
      const companies = next.companies ?? []
      setSelectedId((current) => {
        if (current && companies.some((item) => item.id === current)) return current
        return companies[0]?.id ?? ''
      })
    } catch (err) {
      setError(err instanceof Error ? err.message : t('Не вдалося завантажити реквізити'))
    } finally {
      setBusy(false)
    }
  }, [token])

  useEffect(() => {
    void load()
  }, [load, refreshEpoch])

  const companies = data?.companies?.length
    ? data.companies
    : data
      ? [
          {
            id: 'current',
            title: '',
            legal_name: data.legal_name,
            extras: data.extras,
            fields: data.fields,
            formatted_text: data.formatted_text,
            empty: data.empty,
          },
        ]
      : []
  const selected = useMemo(
    () => companies.find((item) => item.id === selectedId) ?? companies[0] ?? null,
    [companies, selectedId],
  )

  async function insert(text: string, label: string) {
    if (!text.trim()) return
    setStatus(null)
    const ok = await insertTextAtCursor(text, true)
    if (ok) {
      setStatus(t('Вставлено: {label}.', { label }))
      return
    }
    const copied = await copyToClipboard(text)
    setStatus(
      copied
        ? t('Не вдалося вставити. «{label}» скопійовано — вставте вручну.', { label })
        : t('Не вдалося вставити «{label}».', { label }),
    )
  }

  if (error && !data) {
    return (
      <div className="requisites-panel">
        <p className="error">{error}</p>
        <button type="button" className="secondary" onClick={() => void load()}>{t('Повторити')}</button>
      </div>
    )
  }

  if (!data) {
    return <p className="muted">{t('Завантаження реквізитів…')}</p>
  }

  if (data.empty) {
    return (
      <div className="requisites-panel">
        <p className="lead">{t('Реквізити компанії ще не заповнені.')}</p>
        <p className="muted">
          {t(
            'Відкрийте кабінет, розділ «Реквізити», збережіть дані — і вони зʼявляться тут для вставки в документ.',
          )}
        </p>
        <a className="cabinet-link" href={`${CABINET_URL}/requisites`} target="_blank" rel="noreferrer">{t('Відкрити кабінет')}</a>
      </div>
    )
  }

  if (!selected) {
    return <p className="muted">{t('Немає компанії для вставки.')}</p>
  }

  return (
    <div className="requisites-panel">
      <p className="lead">
        {t(
          'Поставте курсор у документ і вставте весь блок або окреме поле. Вставка йде зі змінами, що відстежуються.',
        )}
      </p>
      {error ? <p className="error">{error}</p> : null}
      {status ? <p className="muted">{status}</p> : null}

      {companies.length > 1 ? (
        <label className="company-pick">{t('Компанія')}<select
            aria-label={t('Компанія')}
            value={selected.id}
            onChange={(e) => setSelectedId(e.target.value)}
          >
            {companies.map((item, index) => (
              <option key={item.id} value={item.id}>
                {companyLabel(item, index)}
              </option>
            ))}
          </select>
        </label>
      ) : null}

      {selected.formatted_text ? (
        <section className="card">
          <h2 className="group-head">{t('Блок реквізитів')}</h2>
          <pre className="requisites-preview">{selected.formatted_text}</pre>
          <div className="row wrap">
            <button
              type="button"
              onClick={() => void insert(selected.formatted_text, t('блок реквізитів'))}
            >{t('Вставити блок')}</button>
            <button
              type="button"
              className="secondary"
              onClick={() => void load()}
              disabled={busy}
            >
              {busy ? t('Оновлюю…') : t('Оновити')}
            </button>
          </div>
        </section>
      ) : null}

      <section>
        <h2 className="group-head">{t('Окремі поля')}</h2>
        <ul className="requisites-fields">
          {selected.fields.map((field) => (
            <li key={field.key} className="card req-field">
              <div>
                <p className="muted small">{field.label}</p>
                <p className="req-value">{field.value}</p>
              </div>
              <button
                type="button"
                className="secondary"
                onClick={() => void insert(field.value, field.label)}
              >{t('Вставити')}</button>
            </li>
          ))}
        </ul>
      </section>
    </div>
  )
}
